import { buildAttributionPayload } from '../core/utils.js';

// Captures step-1 data from any `.form-steps__form` the moment the user
// advances past step 1 (validated by the theme's own form-steps.js — this
// component only reacts to the DOM class change, it never re-validates).
// Field mapping is fully config-driven (window.mlatConfig.formSteps.fields),
// set via wp-admin → Leads Attribution → Form Steps, so this file needs no
// per-site changes when step-1 fields differ from one Mekari site to another.

function getAjaxUrl(action) {
  // Prefer the server-baked window.mlatConfig.ajaxUrl over deriving it from
  // <link rel="https://api.w.org/">: on multilingual sites that link can come back
  // locale-prefixed (e.g. /en/wp-json/), and admin-ajax.php is never under that prefix.
  const wpApiLink  = document.querySelector('link[rel="https://api.w.org/"]');
  const wpJsonBase = (wpApiLink && wpApiLink.href)
    ? wpApiLink.href.replace(/\/?$/, '/')
    : (window.location.origin + '/wp-json/');
  const base = (window.mlatConfig && window.mlatConfig.ajaxUrl)
    || (wpJsonBase.replace(/wp-json\/?$/, '').replace(/\/?$/, '/') + 'wp-admin/admin-ajax.php');
  return base + '?action=' + action;
}

function sendToServer(payload) {
  return fetch(getAjaxUrl('mlat_form_steps_store'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
    keepalive: true,
  }).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json().catch(() => null);
  });
}

// Fire-and-forget from the UI's perspective (never blocks step navigation),
// but not silent: one retry on failure, then a console warning so a lost
// write is at least visible during debugging.
function sendWithRetry(payload, attempt) {
  attempt = attempt || 1;
  return sendToServer(payload).catch(err => {
    if (attempt < 2) {
      return new Promise(resolve => setTimeout(resolve, 800)).then(() => sendWithRetry(payload, attempt + 1));
    }
    if (window.__jurnalWabaDebug) console.warn('[mlat form-steps] failed to save step 1 data after retry:', err);
  });
}

function captureFirstStep(wrapper, firstStepEl) {
  const cfg    = (window.mlatConfig && window.mlatConfig.formSteps) || {};
  const fields = cfg.fields || [];
  if (!fields.length) return; // nothing configured in wp-admin — nothing to capture

  const formIdEl = wrapper.querySelector('#form_id, input[name="form_id"]');
  const formId   = formIdEl ? formIdEl.value.trim() : '';
  if (!formId) return; // no key to store the row under — skip rather than write an orphan row

  const payload = buildAttributionPayload();
  delete payload.short; // form_steps table has no `short` column — not needed for this tracker.
  payload.form_id = formId;

  fields.forEach(function (f) {
    const el = firstStepEl.querySelector('[name="' + f.fieldName + '"]');
    payload[f.column] = el ? el.value : '';
  });

  sendWithRetry(payload);
}

export function initFormStepsTracking() {
  // runCoreInits() (waba-core.js) calls every init twice (immediate + after WABA
  // tracking resolves) — guard per-wrapper so we never attach a second observer.
  document.querySelectorAll('.form-steps__form:not([data-mlat-ff-bound])').forEach(function (wrapper) {
    wrapper.setAttribute('data-mlat-ff-bound', '1');

    const steps = wrapper.querySelectorAll('.form-step');
    if (steps.length < 2) return; // only relevant for actual multi-step forms

    const firstStep = steps[0];
    let captured = false;

    const observer = new MutationObserver(function () {
      if (captured || firstStep.classList.contains('form-step--active')) return;
      captured = true;
      observer.disconnect();
      captureFirstStep(wrapper, firstStep);
    });
    observer.observe(firstStep, { attributes: true, attributeFilter: ['class'] });
  });
}
